// pages/Shop/AddShop.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaStore,
  FaBuilding,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaGlobe,
  FaCheckCircle,
  FaImage,
  FaCity,
  FaFlag,
  FaTimes,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa';
import axios from 'axios';
import Swal from 'sweetalert2';
import './Shop.css';

// ✅ SAME BASE URL as Shop.js
const API_BASE = 'https://super-backend.hira-tech.co.uk';

const AddShop = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [citySearch, setCitySearch] = useState('');

  const [formData, setFormData] = useState({
    shop_name: '',
    is_active: true,
    shop_email: '',
    shop_phone: '',
    shop_address: '',
    shop_logo: null,
    country: '',
    cities: []
  });

  const [errors, setErrors] = useState({});
  const [previewLogo, setPreviewLogo] = useState(null);

  // Complete cities data for all countries
  const citiesData = {
    'Pakistan': [
      'Karachi', 'Lahore', 'Islamabad', 'Rawalpindi', 'Faisalabad',
      'Multan', 'Hyderabad', 'Gujranwala', 'Peshawar', 'Quetta',
      'Sialkot', 'Bahawalpur', 'Sukkur', 'Larkana', 'Mingora',
      'Sargodha', 'Rahim Yar Khan', 'Jhang', 'Dera Ghazi Khan', 'Gujrat'
    ],
    'United States': [
      'New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix',
      'Philadelphia', 'San Antonio', 'San Diego', 'Dallas', 'San Jose',
      'Austin', 'Jacksonville', 'Fort Worth', 'Columbus', 'Charlotte'
    ],
    'United Kingdom': [
      'London', 'Birmingham', 'Manchester', 'Glasgow', 'Liverpool',
      'Bristol', 'Sheffield', 'Leeds', 'Edinburgh', 'Leicester',
      'Coventry', 'Nottingham', 'Newcastle', 'Belfast', 'Southampton'
    ],
    'Canada': [
      'Toronto', 'Vancouver', 'Montreal', 'Calgary', 'Edmonton',
      'Ottawa', 'Winnipeg', 'Quebec City', 'Hamilton', 'Kitchener',
      'London', 'Victoria', 'Halifax', 'Oshawa', 'Windsor'
    ],
    'Australia': [
      'Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide',
      'Gold Coast', 'Newcastle', 'Canberra', 'Wollongong', 'Hobart',
      'Geelong', 'Townsville', 'Cairns', 'Darwin', 'Toowoomba'
    ],
    'India': [
      'Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Ahmedabad',
      'Chennai', 'Kolkata', 'Surat', 'Pune', 'Jaipur',
      'Lucknow', 'Kanpur', 'Nagpur', 'Indore', 'Thane'
    ],
    'Germany': [
      'Berlin', 'Hamburg', 'Munich', 'Cologne', 'Frankfurt',
      'Stuttgart', 'Düsseldorf', 'Leipzig', 'Dortmund', 'Essen',
      'Bremen', 'Dresden', 'Hannover', 'Nuremberg', 'Duisburg'
    ],
    'France': [
      'Paris', 'Marseille', 'Lyon', 'Toulouse', 'Nice',
      'Nantes', 'Strasbourg', 'Montpellier', 'Bordeaux', 'Lille',
      'Rennes', 'Reims', 'Saint-Étienne', 'Toulon', 'Grenoble'
    ],
    'Spain': [
      'Madrid', 'Barcelona', 'Valencia', 'Seville', 'Zaragoza',
      'Málaga', 'Murcia', 'Palma', 'Las Palmas', 'Bilbao',
      'Alicante', 'Córdoba', 'Valladolid', 'Vigo', 'Gijón'
    ],
    'Italy': [
      'Rome', 'Milan', 'Naples', 'Turin', 'Palermo',
      'Genoa', 'Bologna', 'Florence', 'Bari', 'Catania',
      'Venice', 'Verona', 'Messina', 'Padua', 'Trieste'
    ],
    'China': [
      'Shanghai', 'Beijing', 'Guangzhou', 'Shenzhen', 'Tianjin',
      'Chongqing', 'Chengdu', 'Nanjing', 'Wuhan', 'Hangzhou',
      'Xi\'an', 'Suzhou', 'Harbin', 'Taiyuan', 'Jinan'
    ],
    'Japan': [
      'Tokyo', 'Yokohama', 'Osaka', 'Nagoya', 'Sapporo',
      'Kobe', 'Kyoto', 'Fukuoka', 'Kawasaki', 'Saitama',
      'Hiroshima', 'Sendai', 'Kitakyushu', 'Chiba', 'Sakai'
    ],
    'Brazil': [
      'São Paulo', 'Rio de Janeiro', 'Brasília', 'Salvador', 'Fortaleza',
      'Belo Horizonte', 'Manaus', 'Curitiba', 'Recife', 'Porto Alegre',
      'Belém', 'Goiânia', 'Guarulhos', 'Campinas', 'São Luís'
    ],
    'United Arab Emirates': [
      'Dubai', 'Abu Dhabi', 'Sharjah', 'Al Ain', 'Ajman',
      'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain', 'Khor Fakkan', 'Dibba'
    ],
    'Saudi Arabia': [
      'Riyadh', 'Jeddah', 'Mecca', 'Medina', 'Dammam',
      'Khobar', 'Tabuk', 'Buraydah', 'Khamis Mushait', 'Al Hufuf'
    ],
    'Turkey': [
      'Istanbul', 'Ankara', 'Izmir', 'Bursa', 'Antalya',
      'Adana', 'Gaziantep', 'Konya', 'Mersin', 'Diyarbakir'
    ],
    'Malaysia': [
      'Kuala Lumpur', 'George Town', 'Ipoh', 'Johor Bahru', 'Shah Alam',
      'Petaling Jaya', 'Kuching', 'Kota Kinabalu', 'Malacca', 'Alor Setar'
    ],
    'Singapore': [
      'Singapore', 'Bedok', 'Jurong East', 'Tampines', 'Woodlands',
      'Bukit Batok', 'Punggol', 'Sengkang', 'Yishun', 'Choa Chu Kang'
    ],
    'Bangladesh': [
      'Dhaka', 'Chittagong', 'Khulna', 'Rajshahi', 'Sylhet',
      'Barisal', 'Rangpur', 'Comilla', 'Narayanganj', 'Mymensingh'
    ],
    'Sri Lanka': [
      'Colombo', 'Kandy', 'Galle', 'Jaffna', 'Negombo',
      'Trincomalee', 'Batticaloa', 'Anuradhapura', 'Ratnapura', 'Badulla'
    ],
    'Nepal': [
      'Kathmandu', 'Pokhara', 'Lalitpur', 'Biratnagar', 'Bharatpur',
      'Birgunj', 'Butwal', 'Dharan', 'Hetauda', 'Janakpur'
    ]
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    setLoadingCountries(true);

    const fallbackCountries = Object.keys(citiesData)
      .map((countryName) => ({
        code: countryName.substring(0, 2).toUpperCase(),
        name: countryName
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    try {
      const response = await axios.get(`${API_BASE}/user/countries/`);

      let list = response.data;
      if (list && !Array.isArray(list)) {
        list = list.results || list.data || list.countries || null;
      }

      if (Array.isArray(list) && list.length > 0) {
        const normalized = list
          .map((c) => {
            if (typeof c === 'string') {
              return { code: c.slice(0, 2).toUpperCase(), name: c };
            }
            const name = c?.name || c?.country || c?.title || '';
            const code =
              c?.code || c?.iso_code || c?.iso2 || name.slice(0, 2).toUpperCase();
            return { code, name };
          })
          .filter((c) => c.name);

        setCountries(normalized);
      } else {
        setCountries(fallbackCountries);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
      setCountries(fallbackCountries);
    } finally {
      setLoadingCountries(false);
    }
  };

  const handleCountryChange = (e) => {
    const countryName = e.target.value;
    setFormData((prev) => ({
      ...prev,
      country: countryName,
      cities: []
    }));

    if (countryName && citiesData[countryName]) {
      setCities(
        citiesData[countryName].map((city) => ({ name: city, selected: false }))
      );
    } else {
      setCities([]);
    }
    setIsCityDropdownOpen(false);
    setCitySearch('');

    if (errors.country) {
      setErrors((prev) => ({ ...prev, country: '' }));
    }
  };

  const toggleCitySelection = (cityName) => {
    setFormData((prev) => {
      const currentCities = prev.cities || [];
      const isSelected = currentCities.includes(cityName);
      const newCities = isSelected
        ? currentCities.filter((c) => c !== cityName)
        : [...currentCities, cityName];

      if (errors.cities) {
        setErrors((prevErr) => ({ ...prevErr, cities: '' }));
      }

      return { ...prev, cities: newCities };
    });

    setCities((prevCities) =>
      prevCities.map((city) =>
        city.name === cityName ? { ...city, selected: !city.selected } : city
      )
    );
  };

  const removeCity = (cityName) => {
    setFormData((prev) => ({
      ...prev,
      cities: prev.cities.filter((c) => c !== cityName)
    }));

    setCities((prevCities) =>
      prevCities.map((city) =>
        city.name === cityName ? { ...city, selected: false } : city
      )
    );
  };

  const toggleCityDropdown = () => {
    if (formData.country) {
      setIsCityDropdownOpen(!isCityDropdownOpen);
      setCitySearch('');
    }
  };

  const filteredCities = cities.filter((city) =>
    city.name.toLowerCase().includes(citySearch.toLowerCase())
  );

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, shop_logo: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewLogo(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.shop_name.trim()) {
      newErrors.shop_name = 'Shop name is required';
    }

    if (!formData.country) {
      newErrors.country = 'Please select a country';
    }

    if (formData.cities.length === 0) {
      newErrors.cities = 'Please select at least one city';
    }

    if (formData.shop_email && !/\S+@\S+\.\S+/.test(formData.shop_email)) {
      newErrors.shop_email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      // ✅ SweetAlert for validation errors
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Please fill in all required fields correctly.',
        confirmButtonColor: '#004aad'
      });
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('shop_name', formData.shop_name);
      formDataToSend.append('is_active', formData.is_active ? 'true' : 'false');

      if (formData.shop_email) formDataToSend.append('shop_email', formData.shop_email);
      if (formData.shop_phone) formDataToSend.append('shop_phone', formData.shop_phone);
      if (formData.country) formDataToSend.append('country', formData.country);

      if (formData.cities && formData.cities.length > 0) {
        formDataToSend.append('cities', JSON.stringify(formData.cities));
      } else {
        formDataToSend.append('cities', JSON.stringify([]));
      }

      formDataToSend.append('shop_address', formData.shop_address || '');

      if (formData.shop_logo) {
        formDataToSend.append('shop_logo', formData.shop_logo);
      }

      const response = await axios.post(
        `${API_BASE}/user/register-shop/`,
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`,
          },
        }
      );

      if (response.status === 201 || response.status === 200) {
        window.dispatchEvent(new CustomEvent('shopCreated'));

        // ✅ SweetAlert Success
        await Swal.fire({
          icon: 'success',
          title: 'Shop Created!',
          text: 'Your shop has been created successfully.',
          confirmButtonColor: '#004aad',
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: true
        });

        navigate('/shops');
      }
    } catch (err) {
      console.error('Error creating shop:', err);

      const data = err.response?.data;

      if (data?.errors && typeof data.errors === 'object') {
        const fieldErrors = {};
        Object.keys(data.errors).forEach((k) => {
          const v = data.errors[k];
          fieldErrors[k] = Array.isArray(v) ? v[0] : String(v);
        });
        setErrors(fieldErrors);

        // ✅ SweetAlert for field errors
        const errorMessages = Object.values(fieldErrors).join('\n');
        Swal.fire({
          icon: 'error',
          title: 'Validation Failed',
          text: errorMessages,
          confirmButtonColor: '#004aad'
        });
      } else {
        const msg =
          typeof data === 'string'
            ? data
            : data?.error ||
              data?.detail ||
              data?.message ||
              'Failed to create shop. Please try again.';

        // ✅ SweetAlert for API error
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: msg,
          confirmButtonColor: '#004aad'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-shop-page">
      <div className="add-shop-header">
        <button className="back-button" onClick={() => navigate('/shops')}>
          Back to Shops
        </button>
        <div className="header-title">
          <h1>Add New Shop</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="add-shop-form">
        <div className="form-grid">
          {/* ============== BASIC INFORMATION ============== */}
          <div className="form-section">
            <h3>
              <FaBuilding className="section-icon" />
              Basic Information
            </h3>

            <div className="form-group">
              <label>Shop Name *</label>
              <input
                type="text"
                name="shop_name"
                value={formData.shop_name}
                onChange={handleInputChange}
                placeholder="Enter shop name"
                className={errors.shop_name ? 'error' : ''}
              />
              {errors.shop_name && (
                <span className="error-message">{errors.shop_name}</span>
              )}
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                <span className="checkbox-label">Active Status</span>
              </label>
              <p className="field-hint">Inactive shops won't be accessible</p>
            </div>
          </div>

          {/* ============== CONTACT INFORMATION ============== */}
          <div className="form-section">
            <h3>
              <FaEnvelope className="section-icon" />
              Contact Information
            </h3>

            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                name="shop_email"
                value={formData.shop_email}
                onChange={handleInputChange}
                placeholder="shop@example.com"
                className={errors.shop_email ? 'error' : ''}
              />
              {errors.shop_email && (
                <span className="error-message">{errors.shop_email}</span>
              )}
            </div>

            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                name="shop_phone"
                value={formData.shop_phone}
                onChange={handleInputChange}
                placeholder="Enter Phone Number"
              />
            </div>

            <div className="form-group">
              <label>Street Address</label>
              <textarea
                name="shop_address"
                value={formData.shop_address}
                onChange={handleInputChange}
                placeholder="Enter street address, building number, etc."
                rows="2"
              />
            </div>
          </div>

          {/* ============== LOCATION INFORMATION ============== */}
          <div className="form-section">
            <h3>
              <FaGlobe className="section-icon" />
              Location Information
            </h3>

            <div className="form-group">
              <label>
                <FaFlag className="field-icon" />
                Country *
              </label>
              <select
                name="country"
                value={formData.country}
                onChange={handleCountryChange}
                className={errors.country ? 'error' : ''}
                disabled={loadingCountries}
              >
                <option value="">-- Select Country --</option>
                {Array.isArray(countries) &&
                  countries.map((country, idx) => (
                    <option key={country.code || idx} value={country.name}>
                      {country.name}
                    </option>
                  ))}
              </select>
              {loadingCountries && (
                <p className="field-hint">Loading countries...</p>
              )}
              {errors.country && (
                <span className="error-message">{errors.country}</span>
              )}
            </div>

            <div className="form-group">
              <label>
                <FaCity className="field-icon" />
                Cities / Towns * (Select multiple)
              </label>

              {Array.isArray(formData.cities) && formData.cities.length > 0 && (
                <div className="selected-cities-container">
                  {formData.cities.map((city, index) => (
                    <span key={index} className="city-tag">
                      {city}
                      <button
                        type="button"
                        className="city-tag-remove"
                        onClick={() => removeCity(city)}
                      >
                        <FaTimes />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div className="city-select-wrapper">
                <button
                  type="button"
                  className={`city-dropdown-toggle ${
                    errors.cities ? 'error' : ''
                  }`}
                  onClick={toggleCityDropdown}
                  disabled={!formData.country}
                >
                  <span className="city-dropdown-text">
                    {formData.cities.length > 0
                      ? `${formData.cities.length} city(ies) selected`
                      : formData.country
                      ? 'Select cities...'
                      : 'Please select a country first'}
                  </span>
                  {isCityDropdownOpen ? <FaChevronUp /> : <FaChevronDown />}
                </button>

                {isCityDropdownOpen && formData.country && (
                  <div className="city-dropdown-menu">
                    <div className="city-search">
                      <input
                        type="text"
                        placeholder="Search cities..."
                        className="city-search-input"
                        value={citySearch}
                        onChange={(e) => setCitySearch(e.target.value)}
                      />
                    </div>
                    <div className="city-dropdown-list">
                      {filteredCities.length === 0 ? (
                        <div className="city-dropdown-empty">
                          No cities available
                        </div>
                      ) : (
                        filteredCities.map((city, index) => (
                          <div
                            key={index}
                            className={`city-dropdown-item ${city.selected ? 'selected' : ''}`}
                            onClick={() => toggleCitySelection(city.name)}
                            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                          >
                            <span
                              className="city-name"
                              style={{ color: '#333', fontSize: '14px', flex: 1 }}
                            >
                              {city.name}
                            </span>
                            {city.selected && (
                              <FaCheckCircle style={{ color: '#004aad', marginLeft: 'auto' }} />
                            )}
                          </div>
                        ))
                      )}
                    </div>
                    <div className="city-dropdown-footer">
                      <button
                        type="button"
                        className="city-dropdown-close"
                        onClick={() => setIsCityDropdownOpen(false)}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {!formData.country && (
                <p className="field-hint">
                  Please select a country first to see cities
                </p>
              )}
              {errors.cities && (
                <span className="error-message">{errors.cities}</span>
              )}
            </div>
          </div>

          {/* ============== SHOP LOGO ============== */}
          <div className="form-section">
            <h3>
              <FaImage className="section-icon" />
              Shop Logo
            </h3>

            <div className="logo-upload-container">
              <div className="logo-preview">
                {previewLogo ? (
                  <img
                    src={previewLogo}
                    alt="Shop logo preview"
                    className="logo-preview-image"
                  />
                ) : (
                  <div className="logo-placeholder">
                    <FaStore className="placeholder-icon" />
                    <span>No logo uploaded</span>
                  </div>
                )}
              </div>

              <div className="logo-upload-controls">
                <label className="upload-button">
                  <FaImage />
                  <span>Choose Logo</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </label>
                <p className="field-hint">
                  Recommended size: 200x200px. Max file size: 2MB
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ============== FORM ACTIONS ============== */}
        <div className="form-actions">
          <button
            type="button"
            className="cancel-btn"
            onClick={() => navigate('/shops')}
          >
            Cancel
          </button>
          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? 'Creating...' : 'Create Shop'}
            {!loading && <FaCheckCircle className="btn-icon" />}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddShop;